import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SettingsIcon from '@mui/icons-material/Settings';
import { useNavigate } from 'react-router-dom';

import api from '../../utils/webcall';
import { getErrorMessage } from '../../utils/utils';
import AccessController from '../../components/access_controller';
import { getVizTokens } from '../analytics/vizTokens';
import { StatTile } from '../analytics/components';
import PayrollSettingsDialog from './PayrollSettingsDialog';
import { MONTHS, money, shortDate } from './shared';

function Payroll(props) {
    const { showErrorMessage, showSuccessMessage } = props;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const tokens = useMemo(() => getVizTokens(theme.palette.mode === 'dark'),
        [theme.palette.mode]);
    const navigate = useNavigate();

    const now = new Date();
    // Payroll is normally run for the month just finished.
    const previous = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const [year, setYear] = useState(previous.getFullYear());
    const [month, setMonth] = useState(previous.getMonth() + 1);

    const [payslips, setPayslips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [working, setWorking] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        api.get('/staff/payslips/', { params: { year, month } }).then((res) => {
            setPayslips(res.data.payslips || []);
        }).catch((error) => {
            showErrorMessage(getErrorMessage(error));
        }).finally(() => setLoading(false));
    }, [year, month, showErrorMessage]);

    useEffect(() => { load(); }, [load]);

    const generateAll = () => {
        setWorking(true);
        api.post('/staff/payslips/', { year, month, all_staff: true }).then((res) => {
            showSuccessMessage(`${res.data.count} payslip(s) ready to review`);
            load();
        }).catch((error) => {
            showErrorMessage(getErrorMessage(error));
        }).finally(() => setWorking(false));
    };

    const drafts = payslips.filter((p) => p.status === 'draft');
    const paid = payslips.filter((p) => p.status === 'paid');
    const draftTotal = drafts.reduce((t, p) => t + Number(p.net_payable || 0), 0);
    const paidTotal = paid.reduce((t, p) => t + Number(p.net_payable || 0), 0);

    const years = [];
    for (let y = now.getFullYear(); y >= now.getFullYear() - 3; y -= 1) years.push(y);

    // Taken from the payslips themselves rather than recomputed, so it is the
    // date those payslips are actually governed by. Anyone with extra days held
    // has a later payday than the rest, so the banner only names a date when
    // everyone shares one.
    const paydays = [...new Set(payslips.map((slip) => slip.payday).filter(Boolean))].sort();
    const payday = paydays.length === 1 ? paydays[0] : null;
    // Derived rather than sent: the hold is an employer setting and does not
    // belong on a payslip that the staff member can also read.
    const holdSlip = payday && payslips.find((slip) => slip.payday === payday);
    const holdDays = holdSlip
        ? Math.round((new Date(holdSlip.payday) - new Date(holdSlip.period_end))
            / 86400000)
        : null;

    return (
        <AccessController require="owner">
            <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
                <PayrollSettingsDialog
                    open={settingsOpen} onClose={() => setSettingsOpen(false)}
                    onSaved={() => {
                        setSettingsOpen(false);
                        showSuccessMessage('Payroll settings saved');
                        load();
                    }}
                    showErrorMessage={showErrorMessage} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <IconButton aria-label="Back to staff" onClick={() => navigate('/staff')}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>Payroll</Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <IconButton aria-label="Payroll settings"
                        onClick={() => setSettingsOpen(true)}>
                        <SettingsIcon />
                    </IconButton>
                </Box>

                <Paper variant="outlined" sx={{ p: { xs: 1, sm: 1.5 }, mb: 2 }}>
                    <Grid container spacing={1} alignItems="center">
                        <Grid size={{ xs: 6, sm: 'auto' }}>
                            <FormControl fullWidth size="small" sx={{ minWidth: { sm: 140 } }}>
                                <InputLabel id="month">Month</InputLabel>
                                <Select labelId="month" label="Month" value={month}
                                    onChange={(e) => setMonth(e.target.value)}>
                                    {MONTHS.map((name, index) => (
                                        <MenuItem key={name} value={index + 1}>{name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 'auto' }}>
                            <FormControl fullWidth size="small" sx={{ minWidth: { sm: 110 } }}>
                                <InputLabel id="year">Year</InputLabel>
                                <Select labelId="year" label="Year" value={year}
                                    onChange={(e) => setYear(e.target.value)}>
                                    {years.map((y) => (
                                        <MenuItem key={y} value={y}>{y}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size="grow" sx={{ display: { xs: 'none', sm: 'block' } }} />
                        <Grid size={{ xs: 12, sm: 'auto' }}>
                            <Button variant="contained" fullWidth={isMobile}
                                disabled={working} onClick={generateAll}>
                                {working ? 'Preparing...' : 'Prepare payslips'}
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid size={{ xs: 6, md: 4 }}>
                                <StatTile tokens={tokens} label="Awaiting payment"
                                    value={money(draftTotal)}
                                    sub={`${drafts.length} draft payslip(s)`} />
                            </Grid>
                            <Grid size={{ xs: 6, md: 4 }}>
                                <StatTile tokens={tokens} label="Already paid"
                                    value={money(paidTotal)}
                                    sub={`${paid.length} payslip(s)`} tone="good" />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <StatTile tokens={tokens} label="Total for the month"
                                    value={money(draftTotal + paidTotal)}
                                    sub={`${MONTHS[month - 1]} ${year}`} />
                            </Grid>
                        </Grid>

                        {/* When this month's wages actually fall due. Every
                            payslip in a month shares a payday unless somebody
                            has extra days held. */}
                        {paydays.length > 0 && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                {payday ? (
                                    <>
                                        {MONTHS[month - 1]} wages are payable
                                        on <strong>{shortDate(payday)}</strong>
                                        {holdDays
                                            ? `, ${holdDays} days after the month ends`
                                            : ''}.
                                    </>
                                ) : (
                                    <>
                                        {MONTHS[month - 1]} wages fall due
                                        between <strong>{shortDate(paydays[0])}</strong> and{' '}
                                        <strong>{shortDate(paydays[paydays.length - 1])}</strong>,
                                        because some people have extra days held back.
                                        Each payslip shows its own date.
                                    </>
                                )}{' '}
                                An advance taken before then comes out of that
                                payslip rather than the next one.
                            </Alert>
                        )}

                        {payslips.length === 0 ? (
                            <Alert severity="info">
                                No payslips for {MONTHS[month - 1]} {year} yet. Use
                                "Prepare payslips" to draft one for everyone employed
                                that month - nothing is paid until you confirm each one.
                            </Alert>
                        ) : (
                            <Grid container spacing={1.5}>
                                {payslips.map((slip) => (
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={slip.id}>
                                        <Paper variant="outlined"
                                            onClick={() => navigate(`/staff/payslip/${slip.id}`)}
                                            sx={{ p: 2, height: '100%', cursor: 'pointer',
                                                '&:hover': { backgroundColor: 'action.hover' } }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between',
                                                alignItems: 'flex-start', gap: 1 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                    {slip.staff_name}
                                                </Typography>
                                                <Chip size="small"
                                                    variant={slip.status === 'paid' ? 'filled' : 'outlined'}
                                                    color={slip.status === 'paid' ? 'success' : 'default'}
                                                    label={slip.status === 'paid' ? 'Paid' : 'Draft'} />
                                            </Box>
                                            <Typography sx={{ mt: 1, fontSize: '1.4rem', fontWeight: 700 }}>
                                                {money(slip.net_payable)}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                                                base {money(slip.base_salary)}
                                                {Number(slip.total_deductions) > 0 &&
                                                    ` · less ${money(slip.total_deductions)}`}
                                                {Number(slip.additions) > 0 &&
                                                    ` · plus ${money(slip.additions)}`}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </>
                )}
            </Box>
        </AccessController>
    );
}

export default Payroll;
